// Card "aguardando aprovação" (wireframe .drow.appr / .wf-row--appr) — fonte
// ÚNICA usada pela Dashboard (coluna "Aguardando aprovação") e pela página
// Demandas (DemandRichCard variante appr). Unificação 2026-06-11: antes eram
// duas cópias divergindo; resolver aqui resolve nos dois lugares.
//
// Estrutura: info clicável (código + título + meta do caller) + 3 ações:
//   👁 ver artefato (desabilitado sem URL) · ✓ aprovar · ✕ reprovar.
// Os DIÁLOGOS de aprovar/reprovar continuam no caller (cada tela tem os seus)
// — aqui só a UI do card + handlers.
import { useEffect, useState, type ReactNode } from 'react';
import { Check, Copy, Eye, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Demand } from '@/lib/api';
import { getArtifactUrl } from '@/lib/demand-artifact';

function CopyCodeBtn({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1100);
    return () => clearTimeout(t);
  }, [copied]);
  return (
    <button
      type="button"
      title="Copiar código"
      aria-label={`Copiar ${code}`}
      className={cn('wf-copybtn', copied && 'copied')}
      onClick={(e) => {
        e.stopPropagation();
        void navigator.clipboard?.writeText(code).catch(() => {});
        setCopied(true);
      }}
    >
      {copied ? <Check className="ic" aria-hidden="true" /> : <Copy className="ic" aria-hidden="true" />}
    </button>
  );
}

export interface WaitingApprovalCardProps {
  demand: Demand;
  /** Linha de metadados abaixo do título (cada tela monta a sua). */
  meta?: ReactNode;
  /** data-testid do card (preserva os ids que os testes de cada tela usam). */
  testId?: string;
  onOpen: (d: Demand) => void;
  onApprove: (d: Demand) => void;
  onReject: (d: Demand) => void;
  /** Diálogos do caller (aprovar/reprovar) renderizados dentro do card. */
  children?: ReactNode;
}

export function WaitingApprovalCard({
  demand,
  meta,
  testId,
  onOpen,
  onApprove,
  onReject,
  children,
}: WaitingApprovalCardProps) {
  const code = demand.code ?? demand.id.slice(0, 8);
  const artifactUrl = getArtifactUrl(demand);
  return (
    <div className="wf-row wf-row--appr" data-testid={testId ?? `approval-row-${demand.id}`}>
      <div
        role="button"
        tabIndex={0}
        // ambas as classes: wf-dash.css estiliza .wf-apinfo, wf-demands.css .apinfo
        className="wf-apinfo apinfo"
        onClick={() => onOpen(demand)}
        onKeyDown={(e) => e.key === 'Enter' && onOpen(demand)}
      >
        <div className="wf-apcode">
          <CopyCodeBtn code={code} />
          <span className="wf-row-code">{code}</span>
        </div>
        <span className="wf-row-title">{demand.title}</span>
        {meta}
      </div>
      <div className="wf-apacts">
        <button
          type="button"
          title={artifactUrl ? 'Ver artefato (nova aba)' : 'Artefato indisponível'}
          aria-label={`Ver artefato de ${code}`}
          disabled={!artifactUrl}
          className="wf-iconbtn wf-iconbtn--art"
          style={artifactUrl ? undefined : { opacity: 0.45, cursor: 'not-allowed' }}
          onClick={(e) => {
            e.stopPropagation();
            if (artifactUrl) window.open(artifactUrl, '_blank', 'noopener,noreferrer');
          }}
        >
          <Eye className="ic" aria-hidden="true" />
        </button>
        <button
          type="button"
          title="Aprovar"
          aria-label={`Aprovar ${code}`}
          className="wf-iconbtn wf-iconbtn--ok"
          onClick={(e) => {
            e.stopPropagation();
            onApprove(demand);
          }}
        >
          <Check className="ic" aria-hidden="true" />
        </button>
        <button
          type="button"
          title="Reprovar"
          aria-label={`Reprovar ${code}`}
          className="wf-iconbtn wf-iconbtn--no"
          onClick={(e) => {
            e.stopPropagation();
            onReject(demand);
          }}
        >
          <X className="ic" aria-hidden="true" />
        </button>
      </div>
      {children}
    </div>
  );
}
